import React, {Fragment, useCallback, useEffect, useState} from "react";
import {Button, Card, Col, Form, Row, Spinner, Table} from "react-bootstrap";
import {format} from "date-fns";
import {
    DepartmentJobMapping,
    getDepartmentJobMappings,
} from "../../api-service/department-job-mapping";
import {PaginationComponent} from "../../parts/pagination-component";
import {JobMappingFormModal} from "./job-mapping-form-modal";
import {CsvImportModal} from "./csv-import-modal";

const PER_PAGE = 20;

const columns = [
    {dataField: "JobCode", width: "17%", text: "ジョブコード"},
    {dataField: "JobName", width: "16%", text: "ジョブ名"},
    {dataField: "ParentJobCode", width: "17%", text: "親ジョブコード"},
    {dataField: "ParentJobName", width: "16%", text: "親ジョブ名"},
    {dataField: "EffectiveStartDate", width: "5%", minWidth: "90px", text: "有効開始日"},
    {dataField: "EffectiveEndDate", width: "5%", minWidth: "90px", text: "有効終了日"},
    {dataField: "DepartmentCode", width: "8%", text: "部署コード"},
];

export function DepartmentJobMappingPage() {
    const [isLoading, setIsLoading] = useState(false);
    const [searchData, setSearchData] = useState({
        jobCode: "",
        parentJobCode: "",
        departmentCode: "",
        showDisabled: true,
    });

    const [currentItems, setCurrentItems] = useState<DepartmentJobMapping[]>([]);
    const [paginatedItems, setPaginatedItems] = useState<DepartmentJobMapping[]>([]);
    const [totalRecord, setTotalRecord] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const currentPageOffset = (pageNumber * PER_PAGE) - PER_PAGE;

    const [showFormModal, setShowFormModal] = useState(false);
    const [formModalMode, setFormModalMode] = useState<"create" | "edit">("create");
    const [selectedRecord, setSelectedRecord] = useState<DepartmentJobMapping | null>(null);
    const [showCsvImportModal, setShowCsvImportModal] = useState(false);

    const loadList = useCallback(async () => {
        setIsLoading(true);
        const items = await getDepartmentJobMappings({
            JobCode: searchData.jobCode,
            ParentJobCode: searchData.parentJobCode,
            DepartmentCode: searchData.departmentCode,
            ShowDisabled: searchData.showDisabled,
        });

        setCurrentItems(items);
        setPaginatedItems(items.slice(0, PER_PAGE));
        setTotalRecord(items.length);
        setPageNumber(1);
        setIsLoading(false);
    }, [searchData]);

    useEffect(() => {
        loadList();
    }, []);

    useEffect(() => {
        const tmpItems = [...currentItems];
        setPaginatedItems(tmpItems.splice(currentPageOffset, PER_PAGE));
    }, [pageNumber, currentItems, currentPageOffset]);

    const setPage = (type: string) => {
        switch (type) {
            case "start-page":
                setPageNumber(1);
                break;
            case "next-page":
                setPageNumber(pageNumber + 1);
                break;
            case "prev-page":
                if (pageNumber === 1) return;
                setPageNumber(pageNumber - 1);
                break;
            case "end-page":
                setPageNumber(Math.ceil(totalRecord / PER_PAGE));
                break;
        }
    };

    const formatDate = (value?: number) => (value !== undefined && value !== null ? format(new Date(value * 1000), "yyyy/MM/dd") : "");

    const startOfDay = (unixSeconds: number) => {
        const date = new Date(unixSeconds * 1000);
        date.setHours(0, 0, 0, 0);
        return date.getTime();
    };

    const handleOpenCreateModal = () => {
        setFormModalMode("create");
        setSelectedRecord(null);
        setShowFormModal(true);
    };

    const handleOpenEditModal = (item: DepartmentJobMapping) => {
        setFormModalMode("edit");
        setSelectedRecord(item);
        setShowFormModal(true);
    };

    const handleCloseFormModal = () => {
        setShowFormModal(false);
    };

    const handleFormModalSaved = () => {
        setShowFormModal(false);
        loadList();
    };

    const handleFormModalDeleted = () => {
        setShowFormModal(false);
        loadList();
    };

    const onCheckDisable = (e) => {
        const value = e.target.checked;
        setSearchData({
            ...searchData,
            showDisabled: value
        })
        console.log(searchData.showDisabled);
    }

    return (
        <Fragment>
            <div className="department-job-mapping">
                <div className="mt-3">
                    <Card className="mt-3 card-container">
                        <Card.Body className="page-title">部署ジョブマッピング</Card.Body>
                    </Card>
                    <Form>
                        <Row className="d-flex align-items-center p-3 pb-2">
                            <Col xs={12} xl="auto" className="d-flex align-items-center col-4 pt-2">
                                <Form.Label className="mt-2 me-2 form-search-label">
                                    ジョブコード
                                </Form.Label>
                                <Form.Control
                                    className="station-form-control"
                                    onChange={(e) => setSearchData({...searchData, jobCode: e.target.value})}
                                />
                            </Col>
                            <Col xs={12} xl="auto" className="d-flex align-items-center col-4 pt-2">
                                <Form.Label className="mt-2 me-2 form-search-label">
                                    親ジョブコード
                                </Form.Label>
                                <Form.Control
                                    className="station-form-control"
                                    onChange={(e) => setSearchData({...searchData, parentJobCode: e.target.value})}
                                />
                            </Col>
                            <Col xs={12} xl="auto" className="d-flex align-items-center col-4 pt-2">
                                <Form.Label className="mt-2 me-2 form-search-label">
                                    部署コード
                                </Form.Label>
                                <Form.Control
                                    className="station-form-control"
                                    onChange={(e) => setSearchData({...searchData, departmentCode: e.target.value})}
                                />
                            </Col>
                            <Col xs={12} md={7} lg="auto" className="d-flex align-items-center col-4 pt-2">
                                <Form.Check className="dept-mapping-form-checkbox pe-2"
                                >
                                    <Form.Check.Input
                                        type="checkbox"
                                        id="showDisabledJobs"
                                        className="me-2 form-check-input-new"
                                        checked={searchData.showDisabled}
                                        onChange={onCheckDisable}
                                    />
                                    <Form.Check.Label htmlFor="showDisabledJobs">無効ジョブも表示する</Form.Check.Label>

                                </Form.Check>
                            </Col>
                            <Col xs={4} md="auto" lg="auto" className="align-items-center pt-2">
                                {isLoading ? (
                                    <Button size="sm" className="button-search" disabled>
                                        <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true"/>
                                        検索...
                                    </Button>
                                ) : (
                                    <Button size="sm" className="button-search" onClick={() => loadList()}>
                                        検索
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </Form>
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <Button size="sm" className="me-2 btn-save" onClick={handleOpenCreateModal}>
                                <svg className="mx-1" width="14" height="14" viewBox="0 0 14 14" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path fill-rule="evenodd" clip-rule="evenodd"
                                          d="M8 7.69231H13.0769C13.3231 7.69231 13.5385 7.47692 13.5385 7.23077V6.30769C13.5385 6.06154 13.3231 5.84615 13.0769 5.84615H8C7.81538 5.84615 7.69231 5.72308 7.69231 5.53846V0.461538C7.69231 0.215385 7.47692 0 7.23077 0H6.30769C6.06154 0 5.84615 0.215385 5.84615 0.461538V5.53846C5.84615 5.72308 5.72308 5.84615 5.53846 5.84615H0.461538C0.215385 5.84615 0 6.06154 0 6.30769V7.23077C0 7.47692 0.215385 7.69231 0.461538 7.69231H5.53846C5.72308 7.69231 5.84615 7.81538 5.84615 8V13.0769C5.84615 13.3231 6.06154 13.5385 6.30769 13.5385H7.23077C7.47692 13.5385 7.69231 13.3231 7.69231 13.0769V8C7.69231 7.81538 7.81538 7.69231 8 7.69231Z"
                                          fill="white"/>
                                </svg>

                                新規追加
                            </Button>
                            <Button className="bg-white" style={{color:"#0070D2"}} size="sm" onClick={() => setShowCsvImportModal(true)}>
                                Excelで一括追加
                            </Button>
                        </div>
                        <div className="d-flex align-items-center">
                            {totalRecord > 0 && (
                                <p className="fs-13 mb-0">{currentPageOffset + paginatedItems.length}/{totalRecord}件</p>
                            )}
                        </div>
                    </div>
                    {paginatedItems.length > 0 ? (
                        <Table className="mt-3 rounded-0 table-mapping-dept">
                            <thead>
                            <tr>
                                {columns.map((col, index) => (
                                    <th key={index} style={{width: col.width ,minWidth:col?.minWidth}}>
                                        {col.text}
                                    </th>
                                ))}
                                <th style={{width: "6%"}}></th>
                            </tr>
                            </thead>
                            <tbody>
                            {
                                paginatedItems.map((item, key) => {
                                    const today = startOfDay(Math.floor(Date.now() / 1000));
                                    const isInvalid = today > startOfDay(item.EffectiveEndDate) || today < startOfDay(item.EffectiveStartDate);
                                    return (
                                        <tr key={key} className={isInvalid ? "expired-row" : ""}>
                                            <td><div className="cell-wrap-2">{item.JobCode}</div></td>
                                            <td><div className="cell-wrap-2">{item.JobName}</div></td>
                                            <td><div className="cell-wrap-2">{item.ParentJobCode}</div></td>
                                            <td><div className="cell-wrap-2">{item.ParentJobName}</div></td>
                                            <td>{formatDate(item.EffectiveStartDate)}</td>
                                            <td>{formatDate(item.EffectiveEndDate)}</td>
                                            <td><div className="cell-wrap-2">{item.DepartmentCode}</div></td>
                                            <td>
                                                <Button
                                                    variant="light"
                                                    className="btn-dept-action"
                                                    size="sm"
                                                    onClick={() => handleOpenEditModal(item)}
                                                >
                                                    編集
                                                </Button>
                                            </td>
                                        </tr>
                                    )
                                })
                            }
                            </tbody>
                        </Table>
                    ) : (
                        <h6 className="text-center pt-5 fw-bold">
                            {!isLoading ? "データがありません。" : "検索中・・・・"}
                        </h6>
                    )}
                    {currentItems.length > 0 && (
                        <PaginationComponent
                            totalPage={Math.ceil(totalRecord / PER_PAGE)}
                            handlePage={setPage}
                            currentPage={pageNumber}
                        />
                    )}
                </div>
            </div>
            <JobMappingFormModal
                show={showFormModal}
                mode={formModalMode}
                initialData={selectedRecord}
                onClose={handleCloseFormModal}
                onSaved={handleFormModalSaved}
                onDeleted={handleFormModalDeleted}
            />
            <CsvImportModal
                show={showCsvImportModal}
                onClose={() => setShowCsvImportModal(false)}
            />
        </Fragment>
    );
}
